'use client'

import * as React from 'react'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'

import { cn } from './utils'
import { Button } from './Button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

interface Item {
  value: string
  label: string
}

interface ComboxPropsBase {
  items: Item[]
  placeholder?: string
  disableSearch?: boolean
}

interface ComboxPropsSingle extends ComboxPropsBase {
  multiselect?: false
  value?: string
  onValueChange?: (value: string) => void
}

interface ComboxPropsMulti extends ComboxPropsBase {
  multiselect: true
  value?: string[]
  onValueChange?: (value: string[]) => void
}

type ComboxProps = ComboxPropsSingle | (ComboxPropsMulti & { className?: string })

export function Combox(props: ComboxProps) {
  const { items, placeholder, disableSearch, multiselect, className } = props
  const [open, setOpen] = React.useState(false)

  if (multiselect) {
    const { value: controlledValue, onValueChange } = props as ComboxPropsMulti
    const [internalValue, setInternalValue] = React.useState<string[]>([])
    const value = controlledValue !== undefined ? controlledValue : internalValue

    const handleValueChange = (newValue: string) => {
      const updatedValue = value.includes(newValue) ? value.filter(v => v !== newValue) : [...value, newValue]

      if (onValueChange) {
        onValueChange(updatedValue)
      } else {
        setInternalValue(updatedValue)
      }
    }

    const getDisplayText = () => {
      if (value.length === 0) return placeholder
      if (value.length === 1) {
        return items.find(item => item.value === value[0])?.label
      }
      if (value.length === 2) {
        const labels = value.map(v => items.find(item => item.value === v)?.label).filter(Boolean)
        return labels.join(', ')
      }
      // For 3+ items, show count
      return `${value.length} položek vybráno`
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('justify-between text-left bg-gray-100', className)}
            size="lg">
            <span className="truncate">{getDisplayText()}</span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            {!disableSearch && <CommandInput placeholder={placeholder} />}
            <CommandList>
              <CommandEmpty>Nenalezeny žádné položky.</CommandEmpty>
              <CommandGroup>
                {items.map(item => (
                  <CommandItem
                    key={item.label}
                    onSelect={() => {
                      handleValueChange(item.value)
                      // Don't close popover in multiselect mode
                    }}>
                    <CheckIcon
                      className={cn('mr-2 h-4 w-4', value.includes(item.value) ? 'opacity-100' : 'opacity-0')}
                    />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  } else {
    // Single select mode
    const { value: controlledValue, onValueChange } = props as ComboxPropsSingle
    const [internalValue, setInternalValue] = React.useState<string>('')
    const value = controlledValue !== undefined ? controlledValue : internalValue

    const handleValueChange = (newValue: string) => {
      if (onValueChange) {
        onValueChange(newValue)
      } else {
        setInternalValue(newValue)
      }
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between bg-gray-100"
            size="lg">
            {value ? items.find(item => item.value === value)?.label : placeholder}
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            {!disableSearch && <CommandInput placeholder={placeholder} />}
            <CommandList>
              <CommandEmpty>Nenalezeny žádné položky.</CommandEmpty>
              <CommandGroup>
                {items.map(item => (
                  <CommandItem
                    key={item.label}
                    onSelect={() => {
                      handleValueChange(item.value)
                      setOpen(false)
                    }}>
                    <CheckIcon className={cn('mr-2 h-4 w-4', value === item.value ? 'opacity-100' : 'opacity-0')} />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
}
